import { useEffect, useState } from "react";
import { Trophy, Users, Calendar, DollarSign, Award, Loader2, Check, Clock } from "lucide-react";
import { toast } from "sonner";
import apiClient, { getErrorMessage } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, FullPageLoader, EmptyState } from "@/components/common";

export default function Tournaments() {
  const { user } = useAuth();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(null);

  const load = () => {
    apiClient.get("/tournaments").then(({ data }) => setTournaments(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const register = async (id) => {
    setRegistering(id);
    try {
      await apiClient.post(`/tournaments/${id}/register`);
      toast.success("Registered! Good luck, champion.");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err, "Registration failed"));
    } finally {
      setRegistering(null);
    }
  };

  if (loading) return <FullPageLoader label="Loading Tournaments" />;

  return (
    <div>
      <PageHeader title="Tournaments" subtitle="Compete in esports events and win the prize pool." />

      {tournaments.length === 0 ? (
        <EmptyState icon={Trophy} title="No tournaments scheduled" subtitle="Check back soon — new competitions drop regularly." />
      ) : (
        <div className="grid md:grid-cols-2 gap-5">
          {tournaments.map((t) => {
            const isRegistered = (t.participants || []).includes(user?.id);
            const full = (t.participants?.length || 0) >= t.max_participants;
            const deadlinePassed = t.registration_deadline && new Date(t.registration_deadline) < new Date();
            const closed = t.status !== "upcoming" || deadlinePassed;
            return (
              <div key={t.id} className="glass glass-hover rounded-2xl p-6 flex flex-col" data-testid={`tournament-${t.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: "rgba(255,176,32,0.15)", border: "1px solid rgba(255,176,32,0.4)" }}>
                      <Trophy className="w-6 h-6" style={{ color: "#ffb020" }} />
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-bold text-white">{t.name}</h3>
                      <p className="text-slate-400 text-sm">{t.game_type}</p>
                    </div>
                  </div>
                  <span className="chip capitalize" style={{ background: "rgba(0,229,255,0.12)", border: "1px solid rgba(0,229,255,0.4)", color: "#00e5ff" }}>{t.status}</span>
                </div>
                {t.description && <p className="text-slate-400 text-sm mb-4">{t.description}</p>}

                <div className="grid grid-cols-2 gap-3 text-sm mb-5">
                  <Info icon={Award} label="Prize Pool" value={`$${t.prize_pool}`} color="#00ff9d" />
                  <Info icon={DollarSign} label="Entry Fee" value={t.entry_fee > 0 ? `$${t.entry_fee}` : "Free"} color="#ffb020" />
                  <Info icon={Users} label="Players" value={`${t.participants?.length || 0}/${t.max_participants}`} color="#00e5ff" />
                  <Info icon={Calendar} label="Starts" value={t.start_datetime ? new Date(t.start_datetime).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "TBD"} color="#ff2e97" />
                </div>

                <div className="mt-auto">
                  {isRegistered ? (
                    <button disabled className="neon-outline-btn w-full py-3" data-testid={`registered-${t.id}`}>
                      <Check className="w-4 h-4" /> Registered
                    </button>
                  ) : (
                    <button disabled={closed || full || registering === t.id} onClick={() => register(t.id)}
                      className="neon-btn w-full py-3" data-testid={`register-${t.id}`}>
                      {registering === t.id ? <><Loader2 className="w-4 h-4 animate-spin" /> Registering...</>
                        : full ? "Tournament Full"
                        : closed ? <><Clock className="w-4 h-4" /> Registration Closed</>
                        : "Register Now"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const Info = ({ icon: Icon, label, value, color }) => (
  <div className="flex items-center gap-2">
    <Icon className="w-4 h-4" style={{ color }} />
    <div>
      <p className="text-slate-500 text-xs">{label}</p>
      <p className="text-white font-semibold">{value}</p>
    </div>
  </div>
);
